import { useEffect, useState } from "react";

/**
 * Subscribes to a CSS media query from JS.
 *
 * Needed where a layout decision cannot be expressed in Tailwind alone — e.g.
 * choosing to open a dialog instead of rendering a panel inline. Prefer plain
 * `md:` classes whenever the difference is purely visual.
 *
 * @param {string} query e.g. "(min-width: 768px)"
 * @param {boolean} defaultValue value used before the first match (and when
 *   `matchMedia` is unavailable); default `true` so server/desktop is assumed.
 */
export function useMediaQuery(query, defaultValue = true) {
	const [matches, setMatches] = useState(() => {
		if (typeof window === "undefined" || !window.matchMedia) return defaultValue;
		return window.matchMedia(query).matches;
	});

	useEffect(() => {
		if (typeof window === "undefined" || !window.matchMedia) return undefined;
		const mediaQuery = window.matchMedia(query);
		const handleChange = (event) => setMatches(event.matches);
		setMatches(mediaQuery.matches);
		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, [query]);

	return matches;
}
